import React, { useRef, useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { useFeaturedVendors } from '../../services/vendorService';
import useUserLocation from '../../hooks/useUserLocation';
import VendorPerformanceBadge from '../client/VendorPerformanceBadge';
import MarketingListingCard from './MarketingListingCard';
import MarketingReveal from './MarketingReveal';

const FeaturedVendors = () => {
  const { userLocation, loading: locationLoading } = useUserLocation();

  const vendorParams = useMemo(() => {
    const params = {
      per_page: 6,
    };

    if (userLocation) {
      const countryParam = userLocation.countryCode || userLocation.country || null;

      if (userLocation.latitude && userLocation.longitude) {
        params.latitude = userLocation.latitude;
        params.longitude = userLocation.longitude;
        if (countryParam) {
          params.country = countryParam;
        }
        params.sort_by = 'distance';
        params.sort_order = 'asc';
      } else if (userLocation.city) {
        params.city = userLocation.city;
        if (countryParam) {
          params.country = countryParam;
        }
      }
      if (!userLocation.latitude && !userLocation.longitude && userLocation.state) {
        params.state = userLocation.state;
      }
      if (countryParam && !params.country) {
        params.country = countryParam;
      }
    }

    return params;
  }, [userLocation]);

  const locationCountry = userLocation?.countryCode || userLocation?.country;
  const hasCompleteLocation = !!(
    userLocation?.latitude &&
    userLocation?.longitude &&
    locationCountry
  );
  const shouldFetch = hasCompleteLocation || (!locationLoading && !userLocation);

  const { data: vendorsResponse, isLoading, error, refetch, isFetching } = useFeaturedVendors(
    vendorParams,
    { enabled: shouldFetch, retry: 2 }
  );
  const scrollRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  const featuredVendors =
    vendorsResponse?.data?.data?.map((vendor) => {
      const city = vendor?.city || '';
      const state = vendor?.state || '';

      return {
        id: vendor.id,
        title: vendor?.business_name || 'Vendor',
        rating: Number(vendor?.average_rating) || 0,
        performanceScore: Number(vendor?.performance_score) || 0,
        platformBookingsCount: Number(vendor?.platform_bookings_count) || 0,
        price: Number(vendor?.min_price) || 0,
        image:
          vendor?.image_url ||
          vendor?.user?.profile_image_url ||
          '/api/placeholder/300/200',
        isFeatured: true,
        location: vendor ? `${city}, ${state}` : 'Location not available',
      };
    }) || [];

  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 400;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    handleScroll();
    if (scrollRef.current) {
      const resizeObserver = new ResizeObserver(() => handleScroll());
      resizeObserver.observe(scrollRef.current);
      return () => resizeObserver.disconnect();
    }
  }, [featuredVendors.length]);

  // Don't render a loud empty block when there are no featured vendors
  if (!isLoading && !error && featuredVendors.length === 0) {
    return null;
  }

  return (
    <section className="relative overflow-hidden border-b border-m-border-light bg-m-surface py-24 sm:py-28">
      <MarketingReveal className="page-container relative z-10">
        <div className="mb-14 flex items-end justify-between gap-4">
          <div className="max-w-xl">
            <h2 className="marketing-section-title mb-4 text-m-ink">
              Featured vendors
            </h2>
            <p className="marketing-body-lg text-m-ink-muted">
              Photo-forward picks near you — verified providers ready to book.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {!isLoading && !error && featuredVendors.length > 0 ? (
              <>
                <button
                  type="button"
                  onClick={() => scroll('left')}
                  disabled={!showLeftArrow}
                  className={`flex h-9 w-9 items-center justify-center rounded-full border transition-colors ${
                    showLeftArrow
                      ? 'border-m-border-light bg-m-surface text-m-ink hover:border-m-ink/30'
                      : 'cursor-not-allowed border-transparent bg-m-canvas-light text-m-ink-faint'
                  }`}
                  aria-label="Scroll left"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => scroll('right')}
                  disabled={!showRightArrow}
                  className={`flex h-9 w-9 items-center justify-center rounded-full border transition-colors ${
                    showRightArrow
                      ? 'border-m-border-light bg-m-surface text-m-ink hover:border-m-ink/30'
                      : 'cursor-not-allowed border-transparent bg-m-canvas-light text-m-ink-faint'
                  }`}
                  aria-label="Scroll right"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </>
            ) : null}
            <Link
              to="/browse-vendors"
              className="group ml-1 hidden items-center gap-1 text-sm font-medium text-m-ink underline-offset-2 hover:underline sm:inline-flex"
            >
              View all
              <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex items-center gap-2 text-m-ink-muted">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm font-medium">Loading featured vendors…</span>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="mb-2 text-lg font-medium text-red-600">
                Failed to load featured vendors
              </div>
              <p className="mb-3 text-sm text-m-ink-muted">Please try again later</p>
              <button
                type="button"
                onClick={() => refetch()}
                className="text-sm font-medium text-m-ink underline underline-offset-2"
                disabled={isFetching}
              >
                {isFetching ? 'Retrying…' : 'Retry'}
              </button>
            </div>
          </div>
        ) : (
          <div className="marketing-rail marketing-rail-surface">
            <div
              ref={scrollRef}
              onScroll={handleScroll}
              className="flex gap-5 overflow-x-auto scroll-smooth pb-4 scrollbar-hide"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {featuredVendors.map((vendor) => (
                <MarketingListingCard
                  key={vendor.id}
                  compact
                  className="w-[min(220px,70vw)] shrink-0 sm:w-[200px]"
                  href={`/vendor/${vendor.id}`}
                  image={vendor.image}
                  title={vendor.title}
                  location={vendor.location}
                  rating={vendor.rating}
                  price={vendor.price}
                  priceSuffix="starting from"
                  featured={vendor.isFeatured}
                  imageOverlay={
                    <VendorPerformanceBadge
                      score={vendor.performanceScore}
                      bookingsCount={vendor.platformBookingsCount}
                    />
                  }
                />
              ))}
            </div>
          </div>
        )}
      </MarketingReveal>
    </section>
  );
};

export default FeaturedVendors;
